import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BuildIcon from "@mui/icons-material/Build";
import MemoryIcon from "@mui/icons-material/Memory";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate, useParams } from "react-router-dom";
import { useDataProvider, useNotify } from "react-admin";
import type { AgentSession, AgentSessionEvent } from "../../models/AgentSession";
import type { AppDataProvider } from "../../types/dataProvider";
import { prettyJson } from "../../utils/agent-event-parser";
import { formatDate } from "../../utils/common";

const POLL_INTERVAL_MS = 2500;
const EVENTS_PAGE_LIMIT = 50;

/**
 * 判断会话是否处于运行中（需短轮询）。
 */
const isSessionRunning = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return (
    normalized === "running"
    || normalized === "processing"
    || normalized === "pending"
    || normalized === "in_progress"
  );
};

/**
 * 判断事件是否为用户侧消息。
 */
const isUserEvent = (event: AgentSessionEvent): boolean => {
  const role = event.role.toLowerCase();
  const type = event.type.toLowerCase();
  return role === "user" || type.includes("user");
};

/**
 * 判断事件是否为智能体侧消息。
 */
const isAgentEvent = (event: AgentSessionEvent): boolean => {
  const role = event.role.toLowerCase();
  const type = event.type.toLowerCase();
  if (
    type.includes("model_request")
    || type.includes("mcp_call")
    || type.includes("tool_call")
    || type.includes("tool_result")
    || type.includes("tool_use")
    || type.includes("session_status")
    || type.includes("status_running")
    || type.includes("status_idle")
    || type.startsWith("span.")
  ) {
    return false;
  }
  return (
    role === "agent"
    || role === "assistant"
    || type.includes("agent.message")
    || type.includes("assistant")
    || (type.includes("agent") && type.includes("message"))
  );
};

/**
 * 合并事件列表并按 id 去重，保持时间顺序。
 */
const mergeEvents = (
  previous: AgentSessionEvent[],
  next: AgentSessionEvent[]
): AgentSessionEvent[] => {
  const map = new Map<string, AgentSessionEvent>();
  for (const event of previous) {
    if (event.id) {
      map.set(event.id, event);
    }
  }
  for (const event of next) {
    if (event.id) {
      map.set(event.id, event);
    } else {
      map.set(`tmp-${map.size}-${event.createdAt}-${event.text}`, event);
    }
  }
  return Array.from(map.values());
};

/**
 * 格式化时间展示。
 */
const formatDateValue = (value: string): string => {
  if (!value) {
    return "";
  }
  return formatDate(value);
};

/**
 * 从会话列表记录或 getOne 兜底解析会话元信息。
 */
const pickSessionMeta = (value: unknown): AgentSession | null => {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const record = value as Record<string, unknown>;
  const dbId = String(record.id ?? record._id ?? "");
  const sessionId = String(record.sessionId ?? record.session_id ?? "");
  const resolvedSessionId = sessionId || (dbId.startsWith("sesn_") ? dbId : "");
  const resolvedId = dbId || resolvedSessionId;
  if (!resolvedId && !resolvedSessionId) {
    return null;
  }
  return {
    id: resolvedId,
    sessionId: resolvedSessionId || resolvedId,
    title: String(record.title ?? record.name ?? ""),
    agentId: String(record.agentId ?? record.agent_id ?? record.agent ?? ""),
    agentName: String(record.agentName ?? record.agent_name ?? ""),
    status: String(record.status ?? record.state ?? ""),
    createdAt: String(record.createdAt ?? record.created_at ?? ""),
    updatedAt: String(record.updatedAt ?? record.updated_at ?? ""),
    raw: record,
  };
};

/**
 * 可折叠的预格式化文本块。
 */
const CollapsiblePre: React.FC<{
  title: string;
  content: string;
  defaultOpen?: boolean;
}> = ({ title, content, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  if (!content) {
    return null;
  }
  return (
    <Box sx={{ mt: 0.75 }}>
      <Button
        size="small"
        color="inherit"
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setOpen((previous) => !previous)}
        sx={{ px: 0.5, minWidth: 0, textTransform: "none" }}
      >
        {title}
      </Button>
      <Collapse in={open}>
        <Box
          component="pre"
          sx={{
            mt: 0.5,
            p: 1,
            m: 0,
            bgcolor: "grey.100",
            borderRadius: 1,
            maxHeight: 240,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 12,
          }}
        >
          {content}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * 模型请求开始事件卡片。
 */
const ModelRequestStartCard: React.FC<{ event: AgentSessionEvent; timeLabel: string }> = ({
  event,
  timeLabel,
}) => {
  const model = event.meta.model;
  const requestId = event.meta.requestId || event.meta.spanId;
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", my: 1 }}>
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, maxWidth: "92%", bgcolor: "info.50" }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <MemoryIcon fontSize="small" color="info" />
          <Chip size="small" color="info" label="模型请求开始" />
          {model ? <Chip size="small" variant="outlined" label={model} /> : null}
          {timeLabel ? (
            <Typography variant="caption" color="text.secondary">
              {timeLabel}
            </Typography>
          ) : null}
        </Stack>
        {requestId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            request_id: {requestId}
          </Typography>
        ) : null}
        <CollapsiblePre title="查看其余 data 字段" content={event.meta.extraDataPretty} />
      </Paper>
    </Box>
  );
};

/**
 * 模型请求结束事件卡片。
 */
const ModelRequestEndCard: React.FC<{ event: AgentSessionEvent; timeLabel: string }> = ({
  event,
  timeLabel,
}) => {
  const model = event.meta.model;
  const requestId = event.meta.requestId;
  const modelRequestId = event.meta.modelRequestId;
  const usage = event.meta.usage;
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", my: 1 }}>
      <Paper
        variant="outlined"
        sx={{
          px: 1.5,
          py: 1,
          maxWidth: "92%",
          bgcolor: event.meta.isError ? "error.50" : "success.50",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <MemoryIcon fontSize="small" color={event.meta.isError ? "error" : "success"} />
          <Chip
            size="small"
            color={event.meta.isError ? "error" : "success"}
            label="模型请求结束"
          />
          {model ? <Chip size="small" variant="outlined" label={model} /> : null}
          {timeLabel ? (
            <Typography variant="caption" color="text.secondary">
              {timeLabel}
            </Typography>
          ) : null}
        </Stack>
        {usage ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
            <Chip size="small" variant="outlined" label={`输入 ${usage.inputTokens}`} />
            <Chip size="small" variant="outlined" label={`输出 ${usage.outputTokens}`} />
            {usage.cacheReadInputTokens > 0 ? (
              <Chip size="small" variant="outlined" label={`缓存读 ${usage.cacheReadInputTokens}`} />
            ) : null}
            {usage.cacheCreationInputTokens > 0 ? (
              <Chip
                size="small"
                variant="outlined"
                label={`缓存写 ${usage.cacheCreationInputTokens}`}
              />
            ) : null}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            无用量数据
          </Typography>
        )}
        {requestId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            request_id: {requestId}
          </Typography>
        ) : null}
        {modelRequestId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            model_request_id: {modelRequestId}
          </Typography>
        ) : null}
        <CollapsiblePre title="查看其余 data 字段" content={event.meta.extraDataPretty} />
      </Paper>
    </Box>
  );
};

/**
 * 汇总 MCP payload 的简短说明。
 */
const summarizeMcpPayloads = (payloads: unknown[]): string => {
  if (!payloads.length) {
    return "";
  }
  const lines: string[] = [];
  for (const payload of payloads) {
    if (typeof payload !== "object" || payload === null) {
      continue;
    }
    const record = payload as Record<string, unknown>;
    if (Array.isArray(record.resultsWithId)) {
      lines.push(`结果列表 ${record.resultsWithId.length} 条`);
      continue;
    }
    const company = record.company_name ?? record.companyName;
    if (typeof company === "string" && company) {
      const phone = record.phone ?? record.contact_phone ?? record.mobile;
      lines.push(
        typeof phone === "string" && phone
          ? `线索：${company}（${phone}）`
          : `线索：${company}`
      );
      continue;
    }
    if (typeof record.id === "string") {
      lines.push(`对象 id: ${record.id}`);
    }
  }
  return lines.join("\n");
};

/**
 * 线索 / 结构化对象预览列表。
 */
const PayloadPreviewList: React.FC<{ payloads: unknown[] }> = ({ payloads }) => {
  if (!payloads.length) {
    return null;
  }
  const items = payloads.slice(0, 8);
  return (
    <Stack spacing={0.75} sx={{ mt: 0.75 }}>
      {items.map((payload, index) => {
        const record =
          typeof payload === "object" && payload !== null
            ? (payload as Record<string, unknown>)
            : null;
        if (!record) {
          return (
            <Typography key={index} variant="caption" color="text.secondary">
              {String(payload)}
            </Typography>
          );
        }
        const company = String(record.company_name ?? record.companyName ?? "");
        const phone = String(record.phone ?? record.contact_phone ?? record.mobile ?? "");
        const name = String(record.name ?? record.contact_name ?? record.contactName ?? "");
        if (company || phone || name) {
          return (
            <Paper key={index} variant="outlined" sx={{ px: 1, py: 0.75, bgcolor: "background.paper" }}>
              <Typography variant="body2" fontWeight={600}>
                {company || name || "线索"}
              </Typography>
              {name && company ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  联系人：{name}
                </Typography>
              ) : null}
              {phone ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  电话：{phone}
                </Typography>
              ) : null}
            </Paper>
          );
        }
        if (Array.isArray(record.resultsWithId)) {
          return (
            <Typography key={index} variant="body2" color="text.secondary">
              含结果列表 {record.resultsWithId.length} 条
              {record.itemCount != null ? `（itemCount=${String(record.itemCount)}）` : ""}
            </Typography>
          );
        }
        return (
          <Box
            key={index}
            component="pre"
            sx={{
              m: 0,
              p: 1,
              bgcolor: "grey.100",
              borderRadius: 1,
              fontSize: 11,
              maxHeight: 120,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {prettyJson(payload)}
          </Box>
        );
      })}
      {payloads.length > 8 ? (
        <Typography variant="caption" color="text.secondary">
          另有 {payloads.length - 8} 条未展开，见下方 JSON
        </Typography>
      ) : null}
    </Stack>
  );
};

/**
 * MCP / 工具输出事件卡片。
 */
const McpCallOutputCard: React.FC<{ event: AgentSessionEvent; timeLabel: string }> = ({
  event,
  timeLabel,
}) => {
  const toolName = event.meta.toolName || "MCP 工具";
  const callId = event.meta.callId || event.meta.toolUseId;
  const summaryLines = event.meta.mcpSummaryLines?.length
    ? event.meta.mcpSummaryLines
    : event.meta.toolOutput
      ? [event.meta.toolOutput]
      : [];
  const summaryText = summaryLines.join("\n");
  const payloadHint = summarizeMcpPayloads(event.meta.mcpPayloads ?? []);
  const payloadJson = (event.meta.mcpPayloads ?? [])
    .map((item) => prettyJson(item))
    .filter(Boolean)
    .join("\n\n");
  const outputPretty = event.meta.outputPretty;
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", my: 1 }}>
      <Paper
        variant="outlined"
        sx={{
          px: 1.5,
          py: 1,
          maxWidth: "92%",
          bgcolor: event.meta.isError ? "error.50" : "grey.50",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <BuildIcon fontSize="small" color={event.meta.isError ? "error" : "action"} />
          <Chip
            size="small"
            color={event.meta.isError ? "error" : "default"}
            label="MCP 工具输出"
          />
          <Chip size="small" variant="outlined" label={toolName} />
          {timeLabel ? (
            <Typography variant="caption" color="text.secondary">
              {timeLabel}
            </Typography>
          ) : null}
        </Stack>
        {callId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            call_id: {callId}
          </Typography>
        ) : null}
        {summaryText ? (
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {summaryText}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            （无输出摘要）
          </Typography>
        )}
        {payloadHint ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
          >
            {payloadHint}
          </Typography>
        ) : null}
        <PayloadPreviewList payloads={event.meta.mcpPayloads ?? []} />
        <CollapsiblePre
          title="查看结构化结果"
          content={payloadJson && payloadJson !== outputPretty ? payloadJson : ""}
        />
        <CollapsiblePre title="查看完整 output（已解码）" content={outputPretty} />
        <CollapsiblePre title="查看其余 data 字段" content={event.meta.extraDataPretty} />
      </Paper>
    </Box>
  );
};

/**
 * MCP / 工具调用事件卡片。
 */
const McpCallCard: React.FC<{ event: AgentSessionEvent; timeLabel: string }> = ({
  event,
  timeLabel,
}) => {
  const toolName = event.meta.toolName || "MCP 工具";
  const serverLabel = event.meta.serverLabel;
  const callId = event.meta.callId || event.meta.toolUseId;
  const input = event.meta.argumentsPretty || event.meta.toolInput || "";
  const preview = input.length > 400 ? `${input.slice(0, 400)}…` : input;
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", my: 1 }}>
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, maxWidth: "92%", bgcolor: "warning.50" }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <BuildIcon fontSize="small" color="warning" />
          <Chip size="small" color="warning" label="MCP 工具调用" />
          <Chip size="small" variant="outlined" label={toolName} />
          {serverLabel ? <Chip size="small" variant="outlined" label={serverLabel} /> : null}
          {timeLabel ? (
            <Typography variant="caption" color="text.secondary">
              {timeLabel}
            </Typography>
          ) : null}
        </Stack>
        {callId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            call_id: {callId}
          </Typography>
        ) : null}
        {preview ? (
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 120,
              overflow: "auto",
            }}
          >
            {preview}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            （无入参）
          </Typography>
        )}
        {input.length > 400 ? <CollapsiblePre title="查看完整入参" content={input} /> : null}
        <CollapsiblePre title="查看其余 data 字段" content={event.meta.extraDataPretty} />
      </Paper>
    </Box>
  );
};

/**
 * 会话状态事件卡片。
 */
const SessionStatusCard: React.FC<{ event: AgentSessionEvent; timeLabel: string }> = ({
  event,
  timeLabel,
}) => {
  const status = event.meta.sessionStatus || "unknown";
  const stopReason = event.meta.stopReason;
  const usage = event.meta.usage;
  const statusColor =
    status.toLowerCase() === "running" || status.toLowerCase() === "processing"
      ? "info"
      : status.toLowerCase() === "idle" || status.toLowerCase() === "completed"
        ? "success"
        : status.toLowerCase().includes("error") || status.toLowerCase() === "terminated"
          ? "error"
          : "default";

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", my: 1 }}>
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, maxWidth: "92%", bgcolor: "grey.50" }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" color={statusColor as "default" | "info" | "success" | "error"} label="会话状态" />
          <Chip size="small" variant="outlined" label={status} />
          {stopReason ? <Chip size="small" variant="outlined" label={`原因: ${stopReason}`} /> : null}
          {timeLabel ? (
            <Typography variant="caption" color="text.secondary">
              {timeLabel}
            </Typography>
          ) : null}
        </Stack>
        {usage ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
            <Chip size="small" variant="outlined" label={`输入 ${usage.inputTokens}`} />
            <Chip size="small" variant="outlined" label={`输出 ${usage.outputTokens}`} />
          </Stack>
        ) : null}
        {event.text && !event.text.startsWith("会话状态：") ? (
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {event.text}
          </Typography>
        ) : null}
        <CollapsiblePre title="查看其余 data 字段" content={event.meta.extraDataPretty} />
      </Paper>
    </Box>
  );
};

/**
 * 未知 / 系统事件卡片（尽量展示已解码字段）。
 */
const UnknownSystemEventCard: React.FC<{ event: AgentSessionEvent; timeLabel: string }> = ({
  event,
  timeLabel,
}) => {
  const payloads = event.meta.mcpPayloads ?? [];
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", my: 1 }}>
      <Paper
        variant="outlined"
        sx={{
          px: 1.5,
          py: 0.75,
          maxWidth: "92%",
          bgcolor: event.meta.isError || event.type.toLowerCase().includes("error")
            ? "error.50"
            : "grey.50",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            color={event.meta.isError ? "error" : "default"}
            label={event.type || "system"}
          />
          {timeLabel ? (
            <Typography variant="caption" color="text.secondary">
              {timeLabel}
            </Typography>
          ) : null}
        </Stack>
        {event.text ? (
          <Typography
            variant="body2"
            sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {event.text}
          </Typography>
        ) : null}
        <PayloadPreviewList payloads={payloads} />
        <CollapsiblePre title="查看 arguments" content={event.meta.argumentsPretty} />
        <CollapsiblePre title="查看 output" content={event.meta.outputPretty} />
        <CollapsiblePre title="查看其余 data 字段" content={event.meta.extraDataPretty} />
      </Paper>
    </Box>
  );
};

/**
 * 识别并渲染特殊结构化事件。
 */
const renderStructuredEvent = (
  event: AgentSessionEvent,
  timeLabel: string
): React.ReactNode | null => {
  const type = event.type.toLowerCase();
  if (type.includes("model_request_start")) {
    return <ModelRequestStartCard event={event} timeLabel={timeLabel} />;
  }
  if (type.includes("model_request_end")) {
    return <ModelRequestEndCard event={event} timeLabel={timeLabel} />;
  }
  if (
    type.includes("mcp_call_output")
    || type.includes("tool_call_output")
    || type.includes("mcp_tool_result")
    || (type.includes("tool_result") && !type.includes("tool_use"))
  ) {
    return <McpCallOutputCard event={event} timeLabel={timeLabel} />;
  }
  if (
    type.includes("mcp_call")
    || type.includes("tool_call")
    || type.includes("tool_use")
  ) {
    return <McpCallCard event={event} timeLabel={timeLabel} />;
  }
  if (
    type.includes("session_status")
    || type.includes("status_running")
    || type.includes("status_idle")
  ) {
    return <SessionStatusCard event={event} timeLabel={timeLabel} />;
  }
  if (event.meta.sessionStatus) {
    return <SessionStatusCard event={event} timeLabel={timeLabel} />;
  }
  return null;
};

/**
 * 单条事件气泡。
 */
const EventBubble: React.FC<{ event: AgentSessionEvent }> = ({ event }) => {
  const userSide = isUserEvent(event);
  const agentSide = isAgentEvent(event);
  const isMessage = userSide || agentSide;
  const timeLabel = formatDateValue(event.processedAt || event.createdAt);
  const structured = renderStructuredEvent(event, timeLabel);
  if (structured) {
    return <>{structured}</>;
  }

  if (!isMessage) {
    return <UnknownSystemEventCard event={event} timeLabel={timeLabel} />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: userSide ? "flex-end" : "flex-start",
        my: 1,
        gap: 1,
      }}
    >
      {!userSide && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "primary.light",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          <SmartToyIcon fontSize="small" />
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 1,
          maxWidth: "75%",
          bgcolor: userSide ? "primary.main" : "grey.100",
          color: userSide ? "primary.contrastText" : "text.primary",
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {event.text || "(空消息)"}
        </Typography>
        {!userSide ? (
          <>
            <PayloadPreviewList payloads={event.meta.mcpPayloads ?? []} />
            <CollapsiblePre title="查看 arguments" content={event.meta.argumentsPretty} />
            <CollapsiblePre title="查看 output" content={event.meta.outputPretty} />
            <CollapsiblePre title="查看其余 data 字段" content={event.meta.extraDataPretty} />
          </>
        ) : null}
        {timeLabel ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              opacity: 0.75,
              textAlign: "right",
            }}
          >
            {timeLabel}
          </Typography>
        ) : null}
      </Paper>
      {userSide && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "grey.400",
            color: "common.white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          <PersonIcon fontSize="small" />
        </Box>
      )}
    </Box>
  );
};

/**
 * 智能体任务会话详情页（事件时间线 + 发消息 + 短轮询）。
 */
const AgentSessionDetailPage: React.FC = () => {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider() as AppDataProvider;

  const [session, setSession] = React.useState<AgentSession | null>(null);
  const [events, setEvents] = React.useState<AgentSessionEvent[]>([]);
  const [lastEventId, setLastEventId] = React.useState("");
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [error, setError] = React.useState("");
  const timelineRef = React.useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = React.useRef(true);
  const lastEventIdRef = React.useRef("");
  const firstEventIdRef = React.useRef("");

  React.useEffect(() => {
    lastEventIdRef.current = lastEventId;
  }, [lastEventId]);

  React.useEffect(() => {
    firstEventIdRef.current = events[0]?.id ?? "";
  }, [events]);

  /**
   * 拉取会话元信息（优先 getOne，失败则用列表项字段兜底）。
   */
  const loadSessionMeta = React.useCallback(async () => {
    if (!sessionId) {
      return;
    }
    try {
      const response = await dataProvider.getOne("agent/sessions", { id: sessionId });
      const meta = pickSessionMeta(response.data);
      if (meta) {
        setSession(meta);
        return;
      }
    } catch {
      // getOne 可能未实现，忽略
    }
    setSession((previous) =>
      previous ?? {
        id: sessionId,
        sessionId,
        title: sessionId,
        agentId: "",
        agentName: "",
        status: "",
        createdAt: "",
        updatedAt: "",
        raw: {},
      }
    );
  }, [dataProvider, sessionId]);

  /**
   * 从事件结果中同步会话状态字段。
   */
  const applyStatusFromEvents = React.useCallback((nextEvents: AgentSessionEvent[]) => {
    for (const event of nextEvents) {
      const statusFromEvent =
        event.meta?.sessionStatus
        || String(
          (event.raw as { session_status?: unknown; sessionStatus?: unknown }).session_status
            ?? (event.raw as { sessionStatus?: unknown }).sessionStatus
            ?? (event.raw as { status?: unknown }).status
            ?? ""
        );
      if (statusFromEvent) {
        setSession((previous) =>
          previous ? { ...previous, status: statusFromEvent } : previous
        );
      }
    }
  }, []);

  /**
   * 拉取事件（对齐百炼 List Events）：
   * GET /agent/sessions/:sessionId/events?limit&order&after_id|before_id|page&types&created_at[*]
   * - 首次/全量：order=desc&limit，前端反转为时间正序
   * - 增量轮询：after_id + order=asc
   * - 加载更早：before_id + order=asc
   */
  const loadEvents = React.useCallback(
    async (options?: { afterId?: string; prepend?: boolean; silent?: boolean }) => {
      if (!sessionId) {
        return;
      }
      const afterId = options?.afterId;
      const prepend = options?.prepend === true;
      try {
        if (!options?.silent) {
          if (prepend) {
            setLoadingMore(true);
          } else if (!afterId) {
            setLoading(true);
          }
        }
        setError("");

        let response;
        if (afterId) {
          response = await dataProvider.getAgentSessionEvents(sessionId, {
            limit: EVENTS_PAGE_LIMIT,
            order: "asc",
            after_id: afterId,
          });
        } else if (prepend) {
          if (!firstEventIdRef.current) {
            setLoadingMore(false);
            return;
          }
          response = await dataProvider.getAgentSessionEvents(sessionId, {
            limit: EVENTS_PAGE_LIMIT,
            order: "asc",
            before_id: firstEventIdRef.current,
          });
        } else {
          response = await dataProvider.getAgentSessionEvents(sessionId, {
            limit: EVENTS_PAGE_LIMIT,
            order: "desc",
          });
        }

        const result = response.data;
        let nextEvents = result.data ?? [];
        if (!afterId && !prepend) {
          nextEvents = [...nextEvents].reverse();
        }

        setEvents((previous) => {
          const merged = afterId || prepend ? mergeEvents(previous, nextEvents) : nextEvents;
          if (merged.length > 0) {
            firstEventIdRef.current = merged[0].id;
            const newestId = merged[merged.length - 1].id;
            lastEventIdRef.current = newestId;
            setLastEventId(newestId);
          }
          return merged;
        });

        setHasMore(result.hasMore);
        applyStatusFromEvents(nextEvents);
      } catch (err) {
        if (!options?.silent) {
          setError("加载会话事件失败");
          notify("加载会话事件失败", { type: "error" });
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [applyStatusFromEvents, dataProvider, notify, sessionId]
  );

  React.useEffect(() => {
    void loadSessionMeta();
    void loadEvents();
  }, [sessionId, loadSessionMeta, loadEvents]);

  // running 时短轮询增量
  React.useEffect(() => {
    if (!sessionId || !isSessionRunning(session?.status ?? "")) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void loadEvents({
        afterId: lastEventIdRef.current || undefined,
        silent: true,
      });
    }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [session?.status, sessionId, loadEvents]);

  // 贴底滚动
  React.useEffect(() => {
    if (!shouldStickToBottomRef.current || !timelineRef.current) {
      return;
    }
    timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [events]);

  const handleTimelineScroll = () => {
    const node = timelineRef.current;
    if (!node) {
      return;
    }
    const distanceToBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    shouldStickToBottomRef.current = distanceToBottom < 80;
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !sessionId || sending) {
      return;
    }
    try {
      setSending(true);
      await dataProvider.sendAgentSessionMessage(sessionId, { text });
      setDraft("");
      setSession((previous) => (previous ? { ...previous, status: "running" } : previous));
      shouldStickToBottomRef.current = true;
      if (lastEventIdRef.current) {
        await loadEvents({ afterId: lastEventIdRef.current, silent: true });
      } else {
        await loadEvents({ silent: true });
      }
    } catch (err) {
      notify("发送消息失败", { type: "error" });
    } finally {
      setSending(false);
    }
  };

  const running = isSessionRunning(session?.status ?? "");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 96px)", p: 2, gap: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <IconButton onClick={() => navigate("/agent/sessions")} aria-label="返回">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {session?.title || sessionId || "会话详情"}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {(session?.agentName || session?.agentId) && (
              <Typography variant="body2" color="text.secondary">
                智能体：{session.agentName || session.agentId}
              </Typography>
            )}
            {session?.status ? (
              <Chip size="small" label={session.status} color={running ? "info" : "default"} />
            ) : null}
          </Stack>
        </Box>
        <IconButton
          onClick={() => {
            void loadSessionMeta();
            void loadEvents();
          }}
          aria-label="刷新"
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {running ? (
        <Alert severity="info" sx={{ py: 0 }}>
          智能体处理中，正在自动刷新事件...
        </Alert>
      ) : null}

      <Paper
        variant="outlined"
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {hasMore ? (
          <Box sx={{ textAlign: "center", py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
            <Button size="small" disabled={loadingMore} onClick={() => void loadEvents({ prepend: true })}>
              {loadingMore ? "加载中..." : "加载更早消息"}
            </Button>
          </Box>
        ) : null}
        <Box
          ref={timelineRef}
          onScroll={handleTimelineScroll}
          sx={{ flex: 1, overflow: "auto", px: 2, py: 1.5 }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : events.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
              暂无事件，发送一条消息开始对话
            </Typography>
          ) : (
            events.map((event) => <EventBubble key={event.id || `${event.type}-${event.createdAt}`} event={event} />)
          )}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={running ? "智能体处理中，可继续输入，处理完成后发送" : "输入消息..."}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void handleSend();
              }
            }}
            disabled={sending}
          />
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={() => void handleSend()}
            disabled={sending || !draft.trim()}
            sx={{ flexShrink: 0, height: 40 }}
          >
            发送
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default AgentSessionDetailPage;
